import React, { useState } from 'react';
 import { gql, useMutation } from '@apollo/client';
 import { withTranslation } from 'react-i18next';
 import {
  Button,
  Container,
  Box,
  TextField,
  Paper,
  Snackbar,
  Alert,
  Typography,
  Grid,
 } from '@mui/material';
 import Header from '../components/Headers/Header'; // Assuming you have a Header component
 import useGlobalStyles from '../utils/globalStyles'; // Assuming you have global styles

 // GraphQL mutation
 const SEND_PUSH_NOTIFICATION_TO_CUSTOMERS = gql`
  mutation SendPushNotificationToCustomers($message: String!, $phones: [String!]) {
   sendPushNotificationToCustomers(message: $message, phones: $phones)
  }
 `;

 const SendPushNotification = (props) => {
  const { t } = props;
  const globalClasses = useGlobalStyles();
  const [message, setMessage] = useState('');
  const [phonesInput, setPhonesInput] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [sending, setSending] = useState(false);
  const [sendPushNotificationToCustomers, { error }] = useMutation(
   SEND_PUSH_NOTIFICATION_TO_CUSTOMERS,
   {
    onCompleted: (data) => {
     setSending(false);
     setSnackbarMessage(t('Push notification sent successfully'));
     setSnackbarSeverity('success');
     setSnackbarOpen(true);
     setMessage('');
     setPhonesInput('');
    },
    onError: (err) => {
     setSending(false);
     setSnackbarMessage(t('Error sending push notification') + ': ' + err.message);
     setSnackbarSeverity('error');
     setSnackbarOpen(true);
    },
   }
  );

  const handleMessageChange = (event) => {
   setMessage(event.target.value);
  };

  const handlePhonesInputChange = (event) => {
   setPhonesInput(event.target.value);
  };

  const handleSendNotification = () => {
   if (!message.trim() || !phonesInput.trim()) {
    setSnackbarMessage(t('Please enter a message and at least one phone number.'));
    setSnackbarSeverity('warning');
    setSnackbarOpen(true);
    return;
   }

   const phonesArray = phonesInput.split(',').map((phone) => phone.trim());
   setSending(true);
   sendPushNotificationToCustomers({ variables: { message, phones: phonesArray } });
  };

  const handleCloseSnackbar = (event, reason) => {
   if (reason === 'clickaway') {
    return;
   }
   setSnackbarOpen(false);
  };

  return (
   <>
    <Header />
    <Box sx={{ height: 16 }} />
    <Container>
     <Paper sx={{ p: 3, mt: 2, borderRadius: 1, color: 'black' }}>
      <Typography variant="h6" gutterBottom color="black">
       {t('Send Push Notification to Customers')}
      </Typography>
      <Grid container spacing={2} mt={2}>
       <Grid item xs={12}>
        <TextField
         label={t('Message')}
         multiline
         rows={4}
         fullWidth
         value={message}
         onChange={handleMessageChange}
         variant="outlined"
         InputProps={{ style: { color: 'black' } }}
        />
       </Grid>
       <Grid item xs={12}>
        <TextField
         label={t('Phone Numbers (comma-separated)')}
         multiline
         rows={6} // Increased the number of visible rows
         fullWidth
         value={phonesInput}
         onChange={handlePhonesInputChange}
         variant="outlined"
         InputProps={{
          style: {
           color: 'black',
           overflowY: 'auto', // Changed to 'auto' to only show when needed
           // Remove any explicit height setting from InputProps style
          },
         }}
         sx={{
          // Remove maxHeight if it's causing an extra scrollbar
          // maxHeight: '150px', // Example of a slightly larger max height if you still want one
         }}
         helperText={t('Enter phone numbers with country code, separated by commas (e.g., +919876543210, +15551234567)')}
        />
       </Grid>
       <Grid item xs={12} sx={{ mt: 2 }}>
        <Button
         variant="contained"
         color="primary"
         onClick={handleSendNotification}
         disabled={sending}
        >
         {sending ? t('Sending...') : t('Send Notification')}
        </Button>
        {error && (
         <Typography color="error" sx={{ mt: 1 }}>
          {t('GraphQL Error')}: {error.message}
         </Typography>
        )}
       </Grid>
      </Grid>
     </Paper>

     <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
     >
      <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
       {snackbarMessage}
      </Alert>
     </Snackbar>
    </Container>
   </>
  );
 };

 export default withTranslation()(SendPushNotification);
